import { useState } from "react";
import { Cookie } from "lucide-react";
import { Button } from "@/app/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { Switch } from "@/app/components/ui/switch";
import { useConsent, useOpenPreferencesFromEvents } from "./consent-context";

/**
 * Bottom-anchored banner shown on first visit (or after the consent version bumps).
 * Follows ANPD guidance for LGPD (banner with equal-weight accept / reject actions plus
 * granular control) and GDPR/ePrivacy (consent must be granted, not assumed).
 *
 * The dialog sibling handles both the "Preferências" flow and the "Gerenciar cookies" link
 * re-opening the same UI after a decision has already been saved.
 */
export function CookieConsentBanner() {
  const { state, acceptAll, rejectAll, openPreferences, preferencesOpen, closePreferences, update } = useConsent();
  useOpenPreferencesFromEvents();

  const showBanner = !state.decided;

  return (
    <>
      {showBanner ? (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
        >
          <div className="mx-auto max-w-3xl pointer-events-auto bg-card text-card-foreground border border-border shadow-xl rounded-xl p-4 sm:p-5">
            <div className="flex gap-3 items-start">
              <Cookie className="w-5 h-5 text-primary mt-0.5 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Cookies e privacidade</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Usamos cookies essenciais para o funcionamento do akihub. Com sua
                  autorização, também usamos cookies analíticos para entender como o site é
                  utilizado e melhorar sua experiência. Você pode aceitar tudo, recusar os não
                  essenciais ou ajustar preferências a qualquer momento.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                  <Button size="sm" onClick={acceptAll} className="w-full sm:w-auto">
                    Aceitar todos
                  </Button>
                  <Button size="sm" variant="outline" onClick={rejectAll} className="w-full sm:w-auto">
                    Recusar não essenciais
                  </Button>
                  <Button size="sm" variant="ghost" onClick={openPreferences} className="w-full sm:w-auto">
                    Preferências
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <PreferencesDialog
        open={preferencesOpen}
        onOpenChange={(next) => (next ? undefined : closePreferences())}
        initialAnalytics={state.analytics}
        initialMarketing={state.marketing}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAll}
        onSave={update}
      />
    </>
  );
}

interface PreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialAnalytics: boolean;
  initialMarketing: boolean;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: (partial: { analytics: boolean; marketing: boolean }) => void;
}

function PreferencesDialog({
  open,
  onOpenChange,
  initialAnalytics,
  initialMarketing,
  onAcceptAll,
  onRejectAll,
  onSave,
}: PreferencesDialogProps) {
  const [analytics, setAnalytics] = useState<boolean>(initialAnalytics);
  const [marketing, setMarketing] = useState<boolean>(initialMarketing);

  // Re-seed toggles every time the dialog opens so it reflects the latest saved state.
  const keyWhenOpen = open ? `${initialAnalytics}-${initialMarketing}` : "";
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setAnalytics(initialAnalytics);
          setMarketing(initialMarketing);
        }
        onOpenChange(next);
      }}
      key={keyWhenOpen}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preferências de cookies</DialogTitle>
          <DialogDescription>
            Controle quais categorias de cookies podem ser usadas. Cookies essenciais são sempre
            aplicados porque são necessários para o funcionamento do site (login, segurança,
            pagamentos). Você pode atualizar estas preferências a qualquer momento.
          </DialogDescription>
        </DialogHeader>

        <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
          <CategoryRow
            title="Essenciais"
            description="Autenticação, segurança, finalização de compras e preferências da sessão (barra lateral, idioma). Sempre ativos."
            checked
            disabled
          />
          <CategoryRow
            title="Analíticos"
            description="Google Analytics 4 para entender de forma agregada como o site é usado e onde melhorar. Sem identificação individual (IP anonimizado)."
            checked={analytics}
            onCheckedChange={setAnalytics}
          />
          <CategoryRow
            title="Marketing"
            description="Permite mensurar campanhas e remarketing no futuro. Atualmente não servimos anúncios próprios; se reativarmos, respeitaremos sua escolha aqui."
            checked={marketing}
            onCheckedChange={setMarketing}
          />
        </ul>

        <DialogFooter className="pt-2">
          <Button variant="ghost" size="sm" onClick={onRejectAll}>
            Recusar não essenciais
          </Button>
          <Button variant="outline" size="sm" onClick={onAcceptAll}>
            Aceitar todos
          </Button>
          <Button size="sm" onClick={() => onSave({ analytics, marketing })}>
            Salvar preferências
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (next: boolean) => void;
}

function CategoryRow({ title, description, checked, disabled, onCheckedChange }: CategoryRowProps) {
  return (
    <li className="flex items-start justify-between gap-4 p-3 sm:p-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange?.(next)}
        aria-label={title}
        className="mt-0.5"
      />
    </li>
  );
}
